import { useCallback, useEffect, useState } from 'react';
import type { BookingFlowConfig } from './types';
import { clearDraft, saveDraft } from './draftStorage';

export type BookingFlowStatus = 'idle' | 'running' | 'error';

/** Optional starting point supplied by `<BookingFlowResumer>` after it has re-derived the
 *  cart / search results. When absent the flow starts fresh (step 0, `config.initialState`). */
export interface BookingFlowResumeSeed<TState> {
  stepIndex: number;
  state: TState;
}

function defaultShouldPersist<TState>(
  config: BookingFlowConfig<TState>,
  state: TState,
  stepIndex: number
): boolean {
  if (stepIndex > 0) return true;
  const persisted = config.persist?.partialize(state) ?? {};
  return Object.keys(persisted).length > 0;
}

export function useBookingFlow<TState>(
  config: BookingFlowConfig<TState>,
  resume?: BookingFlowResumeSeed<TState>
) {
  const [state, setState] = useState<TState>(() => resume?.state ?? config.initialState);
  const [stepIndex, setStepIndex] = useState(() => resume?.stepIndex ?? 0);
  const [status, setStatus] = useState<BookingFlowStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const currentStep = config.steps[stepIndex];

  const runStep = useCallback(
    async (patch?: Partial<TState>) => {
      setStatus('running');
      setError(null);
      const mergedState = patch ? { ...state, ...patch } : state;
      if (patch) setState(mergedState);
      try {
        const result = await currentStep.run(mergedState);
        setState((prev) => ({ ...prev, ...result }));
        setStepIndex((i) => Math.min(i + 1, config.steps.length - 1));
        setStatus('idle');
        return result;
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    [currentStep, state, config.steps.length]
  );

  const goToStep = useCallback((index: number) => setStepIndex(index), []);

  /** Merges a patch into state without running the current step or advancing —
   *  for steps whose UI makes its own side-effecting API calls (e.g. seat selection's
   *  select/remove calls) and just needs to keep the shared `shoppingCart` in sync. */
  const patchState = useCallback((patch: Partial<TState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setState(config.initialState);
    setStepIndex(0);
    setStatus('idle');
    setError(null);
    if (config.persist) clearDraft(config.persist.key);
  }, [config.initialState, config.persist]);

  /** Persist a compact draft after every settled state/step change (never mid-step — a
   *  `running` status also covers the never-resolving 3DS payment promise, so no
   *  post-payment draft is ever written). */
  useEffect(() => {
    const p = config.persist;
    if (!p || status === 'running') return;
    const gate = p.shouldPersist ?? ((s: TState, i: number) => defaultShouldPersist(config, s, i));
    if (!gate(state, stepIndex)) return;
    saveDraft(p.key, {
      v: p.version,
      stepId: config.steps[stepIndex].id,
      state: p.partialize(state),
      deadline: p.getDeadline?.(state),
      savedAt: Date.now(),
    });
  }, [state, stepIndex, status, config]);

  return {
    state,
    stepIndex,
    currentStep,
    steps: config.steps,
    status,
    error,
    runStep,
    goToStep,
    patchState,
    reset,
    isLastStep: stepIndex === config.steps.length - 1,
  };
}

export type BookingFlow<TState> = ReturnType<typeof useBookingFlow<TState>>;
